import Card from '@/components/UI/Card';
import { formatBooleanFlag, formatEnumLabel, formatMetric } from './helpers';
import MetricPill from './MetricPill';
import type { RuntimeStateRow } from './types';

interface FocusedRuntimeStateCardProps {
    selectedState: RuntimeStateRow | null;
}

const FocusedRuntimeStateCard = ({ selectedState }: FocusedRuntimeStateCardProps) => (
    <Card responsivePadding bodyClassName="space-y-4" title="Focused Runtime State">
        {selectedState ? (
            <>
                <div className="text-sm text-zinc-400">Focused state timestamp: {selectedState.dateTime}</div>

                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                    <MetricPill label="Directional Bias" value={formatEnumLabel(selectedState.state.summary.directionalBias)} />
                    <MetricPill label="Signal Relationship" value={formatEnumLabel(selectedState.state.summary.signalRelationship)} />
                    <MetricPill label="Structure Trend" value={formatEnumLabel(selectedState.state.summary.structureTrend)} />
                    <MetricPill label="Breakout State" value={formatEnumLabel(selectedState.state.summary.breakoutState)} />
                    <MetricPill label="Accumulating Range" value={formatBooleanFlag(selectedState.state.summary.isAccumulatingRange)} />
                    <MetricPill label="Distributing Range" value={formatBooleanFlag(selectedState.state.summary.isDistributingRange)} />
                    <MetricPill
                        label="Bullish Price Confirmed"
                        value={formatBooleanFlag(selectedState.state.summary.isBullishPriceConfirmed)}
                    />
                    <MetricPill
                        label="Bearish Price Confirmed"
                        value={formatBooleanFlag(selectedState.state.summary.isBearishPriceConfirmed)}
                    />
                </div>

                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                    <MetricPill label="OBV Value" value={formatMetric(selectedState.state.line.value, 0)} />
                    <MetricPill label="Signal" value={formatMetric(selectedState.state.line.signal, 0)} />
                    <MetricPill label="Flow Balance 10" value={formatMetric(selectedState.state.line.flowBalance10, 3)} />
                    <MetricPill label="Flow Balance 20" value={formatMetric(selectedState.state.line.flowBalance20, 3)} />
                    <MetricPill label="Flow Balance 50" value={formatMetric(selectedState.state.line.flowBalance50, 3)} />
                    <MetricPill label="Flow Balance 65" value={formatMetric(selectedState.state.line.flowBalance65, 3)} />
                    <MetricPill label="Signal Distance" value={formatMetric(selectedState.state.line.signalDistance, 3)} />
                    <MetricPill label="Local Percentile" value={formatMetric(selectedState.state.line.localPercentile, 3)} />
                </div>

                <div className="grid gap-3 md:grid-cols-3">
                    <MetricPill label="Trade-Backed Volume" value={formatBooleanFlag(selectedState.state.context.hasTradeBackedVolume)} />
                    <MetricPill label="Split-Adjusted Bars" value={formatBooleanFlag(selectedState.state.context.usesSplitAdjustedBars)} />
                    <MetricPill
                        label="Quality Flags"
                        value={selectedState.state.context.qualityFlags === null ? 'n/a' : String(selectedState.state.context.qualityFlags)}
                    />
                </div>

                <div className="rounded-lg border border-zinc-800 bg-zinc-950/70 p-4">
                    <div className="mb-2 text-[11px] uppercase tracking-[0.16em] text-zinc-500">Divergence</div>
                    {selectedState.state.divergence ? (
                        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4 text-sm text-zinc-200">
                            <MetricPill label="Type" value={formatEnumLabel(selectedState.state.divergence.type)} />
                            <MetricPill label="Strength" value={formatEnumLabel(selectedState.state.divergence.strength)} />
                            <MetricPill label="Confirmed" value={formatBooleanFlag(selectedState.state.divergence.isConfirmed)} />
                            <MetricPill label="Expired" value={formatBooleanFlag(selectedState.state.divergence.isExpired)} />
                            <MetricPill label="Bars Since Divergence" value={String(selectedState.state.divergence.barsSinceDivergence)} />
                            <MetricPill label="Price Pivot 1" value={formatMetric(selectedState.state.divergence.pricePivot1, 2)} />
                            <MetricPill label="Price Pivot 2" value={formatMetric(selectedState.state.divergence.pricePivot2, 2)} />
                            <MetricPill label="OBV Pivot 1" value={formatMetric(selectedState.state.divergence.obvPivot1, 0)} />
                            <MetricPill label="OBV Pivot 2" value={formatMetric(selectedState.state.divergence.obvPivot2, 0)} />
                        </div>
                    ) : (
                        <div className="text-sm text-zinc-400">No active divergence on the focused state.</div>
                    )}
                </div>
            </>
        ) : (
            <div className="text-sm text-zinc-400">No focused OBV state was available for this audit window.</div>
        )}
    </Card>
);

export default FocusedRuntimeStateCard;
